import { useState } from 'react';
import { AtSign, Check, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateUser, useCheckUsernameAvailability } from '../../services/httpServices/queries';
import { cn } from '../../lib/utils';

interface ChangeUsernameModalProps {
  user: { id: string; username: string } | null;
  onClose: () => void;
  /** Called after a successful update (e.g. to invalidate a parent query). */
  onSuccess?: () => void;
}

/**
 * Change a user's username with live availability validation.
 * Clicking the backdrop does not dismiss the dialog.
 */
export function ChangeUsernameModal({ user, onClose, onSuccess }: ChangeUsernameModalProps) {
  const updateUser = useUpdateUser();
  const [newUsername, setNewUsername] = useState(user?.username ?? '');

  const trimmed = newUsername.trim();
  const changed = trimmed.length > 0 && trimmed !== user?.username;
  const { data: check, isFetching } = useCheckUsernameAvailability(changed ? trimmed : '', user?.id);
  const available = check?.data?.available;

  if (!user) return null;

  const handleSubmit = async () => {
    if (!trimmed) {
      toast.error('Username is required');
      return;
    }
    if (trimmed === user.username) {
      toast.error('Please enter a different username');
      return;
    }
    if (available === false) {
      toast.error('Username already exists');
      return;
    }
    try {
      await updateUser.mutateAsync({ id: user.id, data: { username: trimmed } });
      toast.success('Username updated successfully');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update username');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
        <h3 className="text-lg font-semibold text-text">Change Username</h3>
        <div>
          <label className="block text-sm font-medium text-text mb-1">New Username</label>
          <div className="relative">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              autoFocus
              className={cn(
                'w-full pl-9 pr-9 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary',
                changed && available === false ? 'border-danger' : 'border-border',
              )}
            />
            {changed && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {isFetching ? (
                  <Loader2 size={16} className="animate-spin text-text-muted" />
                ) : available === false ? (
                  <XCircle size={16} className="text-danger" />
                ) : available === true ? (
                  <Check size={16} className="text-success" />
                ) : null}
              </span>
            )}
          </div>
          {changed && (
            <p className={cn('text-xs mt-1', available === false ? 'text-danger' : 'text-text-muted')}>
              {isFetching
                ? 'Checking availability...'
                : available === false
                  ? 'Username already exists'
                  : available === true
                    ? 'Username is available'
                    : ''}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateUser.isPending || !changed || isFetching || available === false}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {updateUser.isPending && <Loader2 size={16} className="animate-spin" />}
            <Check size={16} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
